/**
 * @file Sélecteur de langue des surfaces client (site public, flux de réservation, widgets).
 * @module common.components.flow-language-selector.FlowLanguageSelector
 *
 * Composant canonique : toute sélection de langue d'interface côté client final
 * passe par lui — header du site public, page publique de réservation, widgets
 * embarqués. Ne pas confondre avec `components/LanguageSelector`, réservé à la
 * prévisualisation de contenu multilingue dans le dashboard.
 *
 * La valeur fermée s'affiche selon `labelDisplay`, trois densités pour trois
 * contextes : `native` (drapeau + nom natif, en-têtes de flux), `code`
 * (drapeau + « FR », barres denses), `flag` (drapeau seul, barres étroites
 * mobiles). La liste déroulante montre toujours drapeau + nom natif.
 *
 * Le composant se masque seul quand la sélection n'a pas de sens : une seule
 * langue disponible, ou langue courante hors liste (évite l'état « valeur hors
 * plage » de MUI Select). Quand `primaryColor` est fourni (sélecteur posé sur
 * un fond coloré), les couleurs s'adaptent au contraste du fond ; sinon texte
 * sombre standard.
 */

import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {styled} from '@mui/material/styles';
import {Select, MenuItem, Box} from '@mui/material';
import {Icon} from '@iconify/react';
import {getContrastTextColor} from 'packages/common/utils/colorUtils';
import {LANGUAGES} from 'packages/common/locales/languageConfig';

/** Couleur de texte clair de référence pour le calcul de contraste. */
const LIGHT_TEXT_COLOR = '#FFFFFF';

/** Dimensions standard des drapeaux (ratio 4:3). */
const FLAG_WIDTH = 20;
const FLAG_HEIGHT = 15;

/** Largeur minimale du select fermé selon la densité d'affichage. */
const MIN_WIDTH_BY_DISPLAY = {native: 120, code: 50, flag: 50};

/**
 * Ancrage et plan du menu déroulant : aligné à droite du select, sans blocage
 * du scroll parent (iframes widgets), au-dessus des en-têtes de widgets
 * (z-index 9999 → menu à 10000).
 */
const MENU_PROPS = {
  disableScrollLock: true,
  anchorOrigin: {vertical: 'bottom', horizontal: 'right'},
  transformOrigin: {vertical: 'top', horizontal: 'right'},
  sx: {zIndex: 10000},
  PaperProps: {sx: {mt: 0.5}},
};

/**
 * Select stylisé pour s'intégrer aux en-têtes et barres des flux clients.
 * `isLightText` adapte bordures et texte aux fonds sombres, `selectMinWidth`
 * suit la densité d'affichage demandée.
 */
const StyledSelect = styled(Select, {
  shouldForwardProp: (prop) => prop !== 'isLightText' && prop !== 'selectMinWidth',
})(({theme, isLightText, selectMinWidth}) => ({
  'minWidth': selectMinWidth,
  'height': 40,
  'backgroundColor': 'transparent',
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    color: isLightText ? 'white' : theme.palette.text.primary,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: isLightText ?
      'rgba(255, 255, 255, 0.3)' :
      theme.palette.divider,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: isLightText ?
      'rgba(255, 255, 255, 0.5)' :
      theme.palette.text.secondary,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: isLightText ?
      'rgba(255, 255, 255, 0.7)' :
      theme.palette.primary.main,
  },
  '& .MuiSvgIcon-root': {
    color: isLightText ? 'white' : theme.palette.text.primary,
  },
}));

/** Conteneur d'une langue (drapeau + libellé alignés). */
const LanguageOption = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

/**
 * Drapeau + libellé d'une langue selon la densité demandée.
 *
 * @param {Object} props
 * @param {string} props.code - Code de langue (fr, en, nl, de)
 * @param {'native'|'code'|'flag'} props.display - Libellé affiché à côté du drapeau
 * @return {JSX.Element|null} Rendu de la langue, ou null si code inconnu
 */
const LanguageDisplay = ({code, display}) => {
  const config = LANGUAGES[code];
  if (!config) return null;

  return (
    <LanguageOption>
      <Icon icon={config.icon} width={FLAG_WIDTH} height={FLAG_HEIGHT} />
      {display === 'native' && <span>{config.label}</span>}
      {display === 'code' && <span>{code.toUpperCase()}</span>}
    </LanguageOption>
  );
};

LanguageDisplay.propTypes = {
  code: PropTypes.string.isRequired,
  display: PropTypes.oneOf(['native', 'code', 'flag']).isRequired,
};

/**
 * Sélecteur de langue des flux publics.
 *
 * @param {Object} props
 * @param {string} props.currentLanguage - Langue actuellement sélectionnée (fr, en, nl, de)
 * @param {string[]} props.availableLanguages - Codes de langues activés pour l'établissement
 * @param {function} props.onLanguageChange - Callback appelé avec le nouveau code de langue
 * @param {string} [props.primaryColor] - Couleur du fond pour le calcul de contraste
 * @param {string} [props.ariaLabel='Select language'] - Label ARIA pour l'accessibilité
 * @param {'native'|'code'|'flag'} [props.labelDisplay='native'] - Densité de la valeur fermée
 * @param {boolean} [props.fullWidth=false] - Occupe toute la largeur (menus/drawers)
 * @return {JSX.Element|null} Sélecteur, ou `null` si non pertinent (une seule langue / langue courante hors liste)
 */
export function FlowLanguageSelector({
  currentLanguage,
  availableLanguages,
  onLanguageChange,
  primaryColor,
  ariaLabel = 'Select language',
  labelDisplay = 'native',
  fullWidth = false,
}) {
  const [open, setOpen] = useState(false);

  // Seules les langues connues de la config sont proposées
  const knownLanguages = (availableLanguages || []).filter((code) => LANGUAGES[code]);

  // Ne rien afficher si le choix n'a pas de sens : une seule langue, ou langue
  // courante hors liste (MUI Select afficherait une valeur hors plage + warning)
  if (knownLanguages.length <= 1 || !knownLanguages.includes(currentLanguage)) {
    return null;
  }

  // primaryColor fourni = sélecteur sur fond coloré : adapter au contraste.
  // Sinon (barres claires), toujours en texte sombre.
  const isLightText = primaryColor ?
    getContrastTextColor(primaryColor) === LIGHT_TEXT_COLOR :
    false;

  const handleChange = (event) => {
    onLanguageChange(event.target.value);
    setOpen(false);
  };

  return (
    <StyledSelect
      value={currentLanguage}
      onChange={handleChange}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      size="small"
      variant="outlined"
      fullWidth={fullWidth}
      aria-label={ariaLabel}
      isLightText={isLightText}
      selectMinWidth={fullWidth ? undefined : MIN_WIDTH_BY_DISPLAY[labelDisplay]}
      renderValue={(value) => <LanguageDisplay code={value} display={labelDisplay} />}
      MenuProps={MENU_PROPS}
    >
      {knownLanguages.map((code) => (
        <MenuItem key={code} value={code}>
          <LanguageDisplay code={code} display="native" />
        </MenuItem>
      ))}
    </StyledSelect>
  );
}

FlowLanguageSelector.propTypes = {
  currentLanguage: PropTypes.string.isRequired,
  availableLanguages: PropTypes.arrayOf(PropTypes.string).isRequired,
  onLanguageChange: PropTypes.func.isRequired,
  primaryColor: PropTypes.string,
  ariaLabel: PropTypes.string,
  labelDisplay: PropTypes.oneOf(['native', 'code', 'flag']),
  fullWidth: PropTypes.bool,
};
